import { useRoutePermissions } from '@bakerweb/client-view-permissions';
import { removeNulls } from '@bakerweb/utils';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PieChartOutline from '@mui/icons-material/PieChartOutline';

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_DASHBOARD = '/bank';

const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  general: {
    dashboard: path(ROOTS_DASHBOARD, '/dashboard'),
    securities: path(ROOTS_DASHBOARD, '/securities'),
    securitiesAll: path(ROOTS_DASHBOARD, '/securities/all'),
    securitiesRiskReward: path(ROOTS_DASHBOARD, '/securities/risk-reward'),
    securitiesBBA: path(ROOTS_DASHBOARD, '/securities/bba'),
    securitiesDataExtract: path(ROOTS_DASHBOARD, '/securities/data-extract'),
    securitiesDataExtractDetails: path(ROOTS_DASHBOARD, '/securities/data-extract/detail/:configId'),
    admin: path(ROOTS_DASHBOARD, '/admin'),
    risk: path(ROOTS_DASHBOARD, '/admin/risk'),
    upload: path(ROOTS_DASHBOARD, '/admin/upload'),
    search: path(ROOTS_DASHBOARD, '/search'),
    positions: path(ROOTS_DASHBOARD, '/positions'),
    apm: path(ROOTS_DASHBOARD, '/apm'),
    cds: path(ROOTS_DASHBOARD, '/cds'),
    loans: path(ROOTS_DASHBOARD, '/loans'),
    reports: path(ROOTS_DASHBOARD, '/reports'),
    muniCredit: path(ROOTS_DASHBOARD, '/muni-credit'),
    muniDashboard: path(ROOTS_DASHBOARD, '/muni-credit/dashboard'),
    muniThresholds: path(ROOTS_DASHBOARD, '/muni-credit/thresholds'),
    cusip: path(ROOTS_DASHBOARD, '/muni-credit/cusip/:positionId'),
    users: path(ROOTS_DASHBOARD, '/users'),
    cashflows: path(ROOTS_DASHBOARD, '/cashflows')
  },
  reports: {
    dailyPricing: path(ROOTS_DASHBOARD, '/reports/daily-pricing')
  },
  pledging: {
    root: path(ROOTS_DASHBOARD, '/pledging'),
    pledgee: path(ROOTS_DASHBOARD, '/pledging/pledgee/reports'),
    safekeeping: path(ROOTS_DASHBOARD, '/pledging/safekeeping/reports'),
    pledgeCodes: path(ROOTS_DASHBOARD, '/pledging/codes'),
    pledgeSafekeeping: path(ROOTS_DASHBOARD, '/pledging/safekeeping')
  }
};

export const useClientSidebarConfig = () => {
  const { hasBba, hasApm, hasCdsOverride, hasPledgingOverride, hasMuniCreditOverride, hasDailyPricing } =
    useRoutePermissions();

  const getItems = () => {
    const reportsChildren = removeNulls([
      {
        title: 'Reports',
        path: PATH_DASHBOARD.general.reports,
        icon: <FolderOpenIcon />
      },
      hasDailyPricing ? { title: 'Daily Pricing', path: PATH_DASHBOARD.reports.dailyPricing } : null
    ]);

    return [
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.dashboard,
        icon: <DashboardIcon />
      },
      {
        title: 'Securities',
        path: PATH_DASHBOARD.general.securities,
        icon: <MonetizationOnIcon />,
        children: [
          { title: 'All Securities', path: PATH_DASHBOARD.general.securitiesAll },
          { title: 'Risk Reward', path: PATH_DASHBOARD.general.securitiesRiskReward },
          { title: 'BBA', path: PATH_DASHBOARD.general.securitiesBBA },
          { title: 'Data Extract', path: PATH_DASHBOARD.general.securitiesDataExtract }
        ]
      },
      hasPledgingOverride || hasBba
        ? {
            title: 'Pledging',
            path: PATH_DASHBOARD.pledging.root,
            icon: <AssuredWorkloadIcon />,
            children: removeNulls([
              { title: 'Pledging', path: PATH_DASHBOARD.pledging.root },
              hasBba ? { title: 'Pledging by Pledgee', path: PATH_DASHBOARD.pledging.pledgee } : null,
              hasBba ? { title: 'Pledging by Safekeeping', path: PATH_DASHBOARD.pledging.safekeeping } : null,
              { title: 'Pledge Codes', path: PATH_DASHBOARD.pledging.pledgeCodes },
              { title: 'Safekeeping', path: PATH_DASHBOARD.pledging.pledgeSafekeeping }
            ])
          }
        : null,
      hasMuniCreditOverride || hasBba || hasApm
        ? {
            title: 'Muni Credit',
            path: PATH_DASHBOARD.general.muniCredit,
            icon: <AccountBalanceIcon />,
            children: [
              { title: 'Dashboard', path: PATH_DASHBOARD.general.muniDashboard },
              { title: 'Thresholds', path: PATH_DASHBOARD.general.muniThresholds }
            ]
          }
        : null,
      reportsChildren.length > 0
        ? {
            title: 'Reports',
            path: PATH_DASHBOARD.general.reports,
            icon: <PieChartOutline />,
            children: reportsChildren
          }
        : null,
      {
        title: 'Cash Flows',
        path: PATH_DASHBOARD.general.cashflows,
        icon: <CurrencyExchangeIcon />
      },
      {
        title: 'APM Supplemental',
        path: PATH_DASHBOARD.general.apm,
        icon: <MonitorHeartIcon />
      },
      {
        title: 'Prospectus Search',
        path: PATH_DASHBOARD.general.search,
        icon: <ManageSearchIcon />
      }
    ];
  };

  return [
    {
      subheader: '',
      items: removeNulls(getItems())
    },
    {
      subheader: 'Broker Dealer Services',
      items: removeNulls([
        hasCdsOverride
          ? {
              title: 'CD Offerings',
              path: PATH_DASHBOARD.general.cds,
              icon: <CardMembershipIcon />
            }
          : null,
        {
          title: 'Loans',
          path: PATH_DASHBOARD.general.loans,
          icon: <HandshakeIcon />
        }
      ])
    }
  ];
};
